import React, { useEffect, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import AddIcon from '@mui/icons-material/Add';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Stack, Typography } from '@mui/material';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import type { MediaImage, PropertyRoomTypeWithImages } from '../types';
import { DEFAULT_SOURCE_ROOM_NAME } from '../utils';

import RoomImagePickerDialog from './PickerDialog';
import RoomImageSlot from './Slot';
import type { RoomImage, RoomImagesByKey } from './utils';

const INITIAL_VISIBLE_ROOM_IMAGE_COUNT = 5;

interface Props {
  hiddenImagePublicIds: Array<string>;
  isMetadataEditable?: boolean;
  mediaImages: Array<MediaImage>;
  onAssignImagesToRoom: (roomId: string, roomName: string, selectedImages: Array<MediaImage>) => void;
  onEditImage: (publicId: string) => void;
  onOpenImagesVideo?: () => void;
  onRemoveImageFromRoom: (publicId: string) => void;
  onToggleHiddenImage: (publicId: string) => void;
  onUpdateImageTitle?: (publicId: string, title: string) => void;
  onUploadImagesToRoom?: (roomId: string, roomName: string, files: Array<File>) => void;
  room: PropertyRoomTypeWithImages;
  roomImages: Array<RoomImage>;
  roomKey: string;
  setRoomImagesByKey: React.Dispatch<React.SetStateAction<RoomImagesByKey>>;
  showRoomDetails?: boolean;
}

export default function ImagesByRoomRow({
  hiddenImagePublicIds,
  isMetadataEditable = false,
  mediaImages,
  onAssignImagesToRoom,
  onEditImage,
  onOpenImagesVideo,
  onRemoveImageFromRoom,
  onToggleHiddenImage,
  onUpdateImageTitle,
  onUploadImagesToRoom,
  room,
  roomImages,
  roomKey,
  setRoomImagesByKey,
  showRoomDetails = true,
}: Props) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [areAllImagesVisible, setAreAllImagesVisible] = useState(false);
  const roomName = room.name || DEFAULT_SOURCE_ROOM_NAME;
  const hasExpandableImages = roomImages.length >= INITIAL_VISIBLE_ROOM_IMAGE_COUNT;
  const visibleRoomImages = areAllImagesVisible ? roomImages : roomImages.slice(0, INITIAL_VISIBLE_ROOM_IMAGE_COUNT);
  const shouldShowPickerSlot = areAllImagesVisible || !hasExpandableImages;
  const canUploadDirectly = !!onUploadImagesToRoom;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (canUploadDirectly && isPickerOpen) {
      setIsPickerOpen(false);
    }
  }, [canUploadDirectly, isPickerOpen]);

  const handleAddImages = (selectedImages: Array<MediaImage>) => {
    onAssignImagesToRoom(roomKey, roomName, selectedImages);
    setRoomImagesByKey((currentRoomImagesByKey) => {
      const selectedPublicIds = new Set(selectedImages.map((image) => image.publicId));
      const nextRoomImagesByKey = Object.fromEntries(
        Object.entries(currentRoomImagesByKey).map(([currentRoomKey, currentRoomImages]) => [
          currentRoomKey,
          currentRoomKey === roomKey
            ? currentRoomImages
            : currentRoomImages.filter((image) => !selectedPublicIds.has(image.publicId)),
        ]),
      );
      const currentRoomImages = nextRoomImagesByKey[roomKey] ?? [];
      const selectedRoomImages = selectedImages.map((image) => ({
        publicId: image.publicId,
        title: image.title,
      }));
      const existingPublicIds = new Set(currentRoomImages.map((image) => image.publicId));
      const newRoomImages = selectedRoomImages.filter((image) => !existingPublicIds.has(image.publicId));

      return {
        ...nextRoomImagesByKey,
        [roomKey]: currentRoomImages.concat(newRoomImages),
      };
    });
  };

  const handleDeleteImage = (publicId: string) => {
    onRemoveImageFromRoom(publicId);
    setRoomImagesByKey((currentRoomImagesByKey) => ({
      ...currentRoomImagesByKey,
      [roomKey]: (currentRoomImagesByKey[roomKey] ?? []).filter((image) => image.publicId !== publicId),
    }));
  };

  const handleUpdateImageTitle = (publicId: string, title: string) => {
    setRoomImagesByKey((currentRoomImagesByKey) => ({
      ...currentRoomImagesByKey,
      [roomKey]: (currentRoomImagesByKey[roomKey] ?? []).map((image) =>
        image.publicId === publicId ? { ...image, title } : image,
      ),
    }));
    onUpdateImageTitle?.(publicId, title);
  };

  const handleUploadImagesToRoom = (files: Array<File>) => {
    onUploadImagesToRoom?.(roomKey, roomName, files);
  };

  const handleUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';
    handleUploadImagesToRoom(files);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) => {
      const currentRoomImages = currentRoomImagesByKey[roomKey] ?? [];
      const oldIndex = currentRoomImages.findIndex((image) => image.publicId === active.id);
      const newIndex = currentRoomImages.findIndex((image) => image.publicId === over.id);

      if (oldIndex < 0 || newIndex < 0) {
        return currentRoomImagesByKey;
      }

      return {
        ...currentRoomImagesByKey,
        [roomKey]: arrayMove(currentRoomImages, oldIndex, newIndex),
      };
    });
  };

  return (
    <Box sx={{ py: showRoomDetails ? 3 : 0 }}>
      {!showRoomDetails && (
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            Carousel order is used on the offer page. Drag images to reorder. Use the empty slot to upload an image.
            Hover an image to edit, hide, or delete.
          </Typography>
          {(!canUploadDirectly || onOpenImagesVideo) && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', lg: 'auto' } }}>
              {!canUploadDirectly && (
                <Button
                  variant="outlined"
                  startIcon={<CollectionsOutlinedIcon />}
                  disabled={mediaImages.length === 0}
                  onClick={() => setIsPickerOpen(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Pick from library
                </Button>
              )}
              {onOpenImagesVideo && (
                <Button
                  variant="text"
                  startIcon={<OpenInNewIcon />}
                  onClick={onOpenImagesVideo}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Open Images & Video
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: showRoomDetails ? { xs: '1fr', lg: '220px minmax(0, 1fr)' } : '1fr',
          gap: { xs: 2, lg: 3 },
        }}
      >
        {showRoomDetails && (
          <Stack spacing={1.5} alignItems="flex-start">
            <Box>
              <Typography variant="h6">{roomName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {roomImages.length} {roomImages.length === 1 ? 'image' : 'images'} selected
              </Typography>
            </Box>
            {canUploadDirectly ? (
              <Button component="label" variant="text" color="primary" startIcon={<AddIcon />}>
                Upload image
                <input
                  hidden
                  accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
                  multiple
                  type="file"
                  onChange={handleUploadInputChange}
                />
              </Button>
            ) : (
              <Button
                variant="text"
                color="primary"
                startIcon={<AddIcon />}
                disabled={mediaImages.length === 0}
                onClick={() => setIsPickerOpen(true)}
              >
                Pick images
              </Button>
            )}
          </Stack>
        )}

        <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
          <Stack spacing={2}>
            <SortableContext items={visibleRoomImages.map((image) => image.publicId)} strategy={rectSortingStrategy}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(5, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {visibleRoomImages.map((image, index) => (
                  <RoomImageSlot
                    key={image.publicId}
                    hiddenImagePublicIds={hiddenImagePublicIds}
                    image={image}
                    isMetadataEditable={isMetadataEditable}
                    onDeleteImage={handleDeleteImage}
                    onEditImage={onEditImage}
                    onOpenPicker={() => setIsPickerOpen(true)}
                    onToggleHiddenImage={onToggleHiddenImage}
                    onUpdateImageTitle={handleUpdateImageTitle}
                    onUploadImages={canUploadDirectly ? handleUploadImagesToRoom : undefined}
                    slotNumber={index + 1}
                  />
                ))}
                {shouldShowPickerSlot && (
                  <RoomImageSlot
                    hiddenImagePublicIds={hiddenImagePublicIds}
                    isMetadataEditable={isMetadataEditable}
                    onDeleteImage={handleDeleteImage}
                    onEditImage={onEditImage}
                    onOpenPicker={() => setIsPickerOpen(true)}
                    onToggleHiddenImage={onToggleHiddenImage}
                    onUpdateImageTitle={handleUpdateImageTitle}
                    onUploadImages={canUploadDirectly ? handleUploadImagesToRoom : undefined}
                    slotNumber={visibleRoomImages.length + 1}
                  />
                )}
              </Box>
            </SortableContext>
            {hasExpandableImages && (
              <Button variant="text" color="primary" onClick={() => setAreAllImagesVisible((isVisible) => !isVisible)}>
                {areAllImagesVisible ? 'Show less' : 'Show more'}
              </Button>
            )}
          </Stack>
        </DndContext>
      </Box>
      <RoomImagePickerDialog
        mediaImages={mediaImages}
        open={isPickerOpen && !canUploadDirectly}
        roomImages={roomImages}
        roomName={roomName}
        onAddImages={handleAddImages}
        onClose={() => setIsPickerOpen(false)}
      />
    </Box>
  );
}
